import {
  AMEX_CVV_LENGTH,
  STANDARD_CVV_LENGTH,
  AMEX_SCHEME,
} from "../constants";

import isValidMonth from "is-valid-month";
import parseIntStrict from "parse-int";
import {
  removeNonDigits,
  replaceFullwidthChars,
  limitToLength,
} from "./string";

export const compose = (...fns) =>
  fns.reduce((f, g) => (...args) => f(g(...args)));

/**
 * Send the state of an element to a controller iframe
 * @param {string} element name of the element sending the state (cardNumber, expiryDate, cvv)
 * @param {Object} data object to send with message
 * @param {string} iframeName iFrame to send the message to
 */
const sendToIframe = (element, data, iframeName) => {
  try {
    window.parent.frames[iframeName].postMessage(
      {
        sender: window.location.href,
        element,
        data,
      },
      window.location.origin
    );
  } catch (i) {
    console.error(i);
  }
};

/**
 * Send the state of an element to the controller iframe
 * @param {string} element name of the element sending the state (cardNumber, expiryDate, cvv)
 * @param {Object} data object to send with message
 */
export const sendToControllerIframe = (element, data) => {
  sendToIframe(element, data, "checkout-frames");
};

/**
 * Send the state of an element to the single frame Form iframe
 * @param {string} element name of the element sending the state (cardNumber, expiryDate, cvv)
 * @param {Object} data object to send with message
 */
export const sendToFormIframe = (element, data) => {
  sendToIframe(element, data, "checkout-frames-form");
};

/**
 * Get the element iFrame name using the element name
 * @param {string} element name of the element for which to get its iFrame
 */
export const getElementIFrameName = (element) => {
  return `checkout-frames-${element}`;
};

/**
 * Send data to an element
 * @param {string} element name of the element to send the data to
 * @param {Object} data data to send
 */
export const sendToElementIframe = (element, data) => {
  try {
    window.parent.frames[getElementIFrameName(element)].postMessage(
      {
        sender: window.location.href,
        receiver: element,
        data,
      },
      window.location.origin
    );
  } catch (i) {
    console.error(i);
  }
};

/**
 * Send data to the merchant page via postMessage
 * @param {string} event event name
 * @param {Object} data data to send
 * @param {string} domain domain of the receiver
 */
export const sendViaPostMessage = (event, data, domain = "*") => {
  window.parent.postMessage(
    {
      type: "cko-msg",
      event,
      data,
    },
    domain
  );
};

/**
 * Tests if the public key is for a QA merchant
 * @param {string} publicKey - key to test
 * @returns {boolean}
 */
export const isQaPublicKey = (publicKey) => {
  const isNasQaPublicKey = /pk_qa_/.test(publicKey);

  const abcQaPublicKeys = ["pk_test_61c0028d-a82c-4b7a-8df9-91def692bccc"];
  const isAbcQaPublicKey = abcQaPublicKeys.includes(publicKey);

  return isNasQaPublicKey || isAbcQaPublicKey;
};

/**
 * Tests if the public key is for a Sandbox merchant.
 * @param {string} publicKey - key to test
 * @returns {boolean}
 */
export const isSandboxPublicKey = (publicKey) =>
  /pk_(test_|sbox_)/.test(publicKey);

export const getCVVMaxLength = (scheme) =>
  scheme === AMEX_SCHEME ? AMEX_CVV_LENGTH : STANDARD_CVV_LENGTH;

export const validateCVV = (oldScheme, scheme, clearCVV) => {
  if (oldScheme === AMEX_SCHEME || scheme === AMEX_SCHEME) {
    clearCVV();
  }
};

export const focusPreviousInput = (previousInput) => {
  return (event) => {
    const value = event.target.value;
    const key = event.keyCode || event.charCode;
    if (value.length === 0 && key === 8) {
      previousInput.focus();
      event.preventDefault();
    }
  };
};

export const forceRedrawElement = (element) => {
  const display = element.style.display;
  element.style.display = "none";
  // const trick = element.offsetHeight
  element.style.display = display;
};

const formatMonth = (month) => {
  return parseIntStrict(month);
};

const isValidYear = (year) => {
  year = parseIntStrict(year);
  return year > 0;
};

const isYearPast = (year) => {
  return new Date().getFullYear() > new Date("1-1-20" + year).getFullYear();
};

const validateMonth = (month) => {
  const formattedMonth = formatMonth(month);
  return isValidMonth(formattedMonth);
};

const validateYear = (year) => {
  return isValidYear(year) && !isYearPast(year);
};

export const isExpiryDateValid = (text) => {
  const [month, year] = text.split("/");
  return validateMonth(month) && validateYear(year) && isDateValid(month, year);
};

function isDateValid(month, year) {
  const date = new Date(parseInt(`20${year}`, 10), parseInt(month, 10));
  return date > new Date();
}

export function formatExpDate(oldDate, newDate) {
  const newValue = compose(
    limitToLength(4),
    handleFullYearPaste,
    removeNonDigits,
    replaceFullwidthChars
  )(newDate);

  if (hasTwoLeadingZeros(newValue)) {
    return "0";
  }

  switch (newValue.length) {
    case 0:
      // "" -> ""
      return "";

    case 1:
      if (newValue > 1) {
        // "2" -> "02"
        return `0${newValue}/`;
      }
      // "1" -> "1"
      return newValue;

    case 2: {
      if (newValue > 12) {
        // 13 -> "01/3"
        const arr = String(newValue).split("");
        return `0${arr[0]}/${arr[1]}`;
      }
      // 10 11 12 -> 10/ 11/ 12/
      return `${newValue}${setDivider(oldDate, newDate)}`;
    }

    case 3: {
      const firstChar = newValue[0];
      if (firstChar > 1) {
        // "325" -> "03/25"
        return `0${firstChar}/${newValue.substring(1)}`;
      }
      // "125" -> "12/5"
      return getMonthYear(newValue);
    }

    default:
      // "123" -> (0,2), (2,len) -> 12/3
      // "1234" -> (0,2), (2,len) -> 12/34
      return getMonthYear(newValue);
  }
}

function handleFullYearPaste(value) {
  if (value.length === 5) {
    // "1/2027" -> "12027" -> "0127"
    return `0${value.substring(0, 1)}${value.substring(3)}`;
  } else if (value.length === 6) {
    // "10/2027" -> "102027" -> "1027"
    return `${value.substring(0, 2)}${value.substring(4)}`;
  }

  return value;
}

function hasTwoLeadingZeros(value) {
  return value.substring(0, 2) === "00";
}

function getMonthYear(d) {
  const month = d.substring(0, 2);
  const year = d.substring(2);
  return `${month}/${year}`;
}

function setDivider(oldDate, newDate) {
  return wasSlashDeleted(oldDate, newDate) ? "" : "/";
}

function wasSlashDeleted(oldDate, newDate) {
  const l = oldDate.length - 1;
  return oldDate.substr(l) === "/" && newDate === oldDate.substr(0, l);
}

export function formatCvv(newCvv, cardScheme) {
  const maxLength = getCVVMaxLength(cardScheme);

  return compose(
    limitToLength(maxLength),
    removeNonDigits,
    replaceFullwidthChars
  )(newCvv);
}
